import { useEffect, useRef } from 'react'
import { useMotionEnabled } from '../../hooks/useMotionEnabled'

export function MeshLattice({ enabled = true, density = 'regular' }: {
  enabled?: boolean; density?: 'compact' | 'regular' | 'comfy'
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const motion = useMotionEnabled()

  useEffect(() => {
    if (!enabled) return
    const canvas = canvasRef.current
    if (!canvas) return

    let raf: number
    let w = 0, h = 0, dpr = 1
    let nodes: Array<{ x: number; y: number; seed: number; phase: number }> = []
    let edges: Array<{ a: typeof nodes[number]; b: typeof nodes[number]; seed: number }> = []
    let packets: Array<{ edge: typeof edges[number]; t: number; speed: number; hue: string; born: number }> = []
    const mouse = { x: -9999, y: -9999 }

    const cellSize = density === 'comfy' ? 90 : density === 'compact' ? 60 : 75

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = window.innerWidth
      h = window.innerHeight
      canvas!.width = w * dpr
      canvas!.height = h * dpr
      canvas!.style.width = w + 'px'
      canvas!.style.height = h + 'px'
      buildGrid()
    }

    function buildGrid() {
      nodes = []
      edges = []
      const cols = Math.ceil(w / cellSize) + 2
      const rows = Math.ceil(h / cellSize) + 2
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const seed = (r * 73 + c * 131) % 1000 / 1000
          const jx = (seed - 0.5) * cellSize * 0.35
          const jy = ((seed * 7) % 1 - 0.5) * cellSize * 0.35
          nodes.push({ x: c * cellSize + jx, y: r * cellSize + jy, seed, phase: seed * Math.PI * 2 })
        }
      }
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c
          const node = nodes[i]
          if (!node) continue
          const right = nodes[r * cols + c + 1]
          const down = nodes[(r + 1) * cols + c]
          const diag = nodes[(r + 1) * cols + c + 1]
          if (right && (node.seed + 0.13) % 1 > 0.35) edges.push({ a: node, b: right, seed: (node.seed + right.seed) / 2 })
          if (down && (node.seed + 0.41) % 1 > 0.45) edges.push({ a: node, b: down, seed: (node.seed + down.seed) / 2 })
          if (diag && (node.seed + 0.77) % 1 > 0.78) edges.push({ a: node, b: diag, seed: (node.seed + diag.seed) / 2 })
        }
      }
      packets = []
      const packetCount = Math.min(3, Math.max(2, Math.floor(edges.length / 60)))
      for (let i = 0; i < packetCount; i++) spawnPacket(true)
    }

    function spawnPacket(initial = false) {
      const edge = edges[Math.floor(Math.random() * edges.length)]
      if (!edge) return
      packets.push({
        edge, t: initial ? Math.random() : 0,
        speed: 0.0008 + Math.random() * 0.0012,
        hue: Math.random() < 0.7 ? 'cyan' : (Math.random() < 0.5 ? 'violet' : 'lime'),
        born: performance.now(),
      })
    }

    function readColor(name: string) {
      const css = getComputedStyle(document.documentElement)
      const isLight = document.documentElement.getAttribute('data-theme') === 'light'
      switch (name) {
        case 'cyan':   return css.getPropertyValue('--accent').trim() || '#00E5FF'
        case 'violet': return '#B79DFF'
        case 'lime':   return css.getPropertyValue('--lime').trim() || '#B8FF3A'
        case 'edge':   return isLight ? 'rgba(60,80,110,0.07)' : 'rgba(180,210,240,0.04)'
        case 'node':   return isLight ? 'rgba(60,80,110,0.12)' : 'rgba(200,220,255,0.10)'
        default:       return '#888'
      }
    }

    let last = performance.now()
    function loop(now: number) {
      const dt = Math.min(now - last, 50)
      last = now
      const ctx = canvas!.getContext('2d')!
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      const edgeColor = readColor('edge')
      const nodeColor = readColor('node')

      ctx.lineWidth = 1
      ctx.strokeStyle = edgeColor
      ctx.beginPath()
      for (const e of edges) {
        ctx.moveTo(e.a.x, e.a.y)
        ctx.lineTo(e.b.x, e.b.y)
      }
      ctx.stroke()

      for (const n of nodes) {
        const dx = n.x - mouse.x, dy = n.y - mouse.y
        const distSq = dx * dx + dy * dy
        const near = distSq < 180 * 180
        const r = near ? 1.7 + (1 - Math.sqrt(distSq) / 180) * 1.5 : 1.1
        ctx.fillStyle = near ? readColor('cyan') : nodeColor
        ctx.beginPath()
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2)
        ctx.fill()
      }

      for (let i = packets.length - 1; i >= 0; i--) {
        const p = packets[i]
        if (motion) p.t += p.speed * dt
        if (p.t >= 1) {
          const candidates = edges.filter(e => e.a === p.edge.b || e.b === p.edge.b)
          if (candidates.length) {
            const next = candidates[Math.floor(Math.random() * candidates.length)]
            const flipped = next.b === p.edge.b
            p.edge = flipped ? { a: next.b, b: next.a, seed: next.seed } : next
            p.t = 0
          } else {
            packets.splice(i, 1)
            spawnPacket()
            continue
          }
        }
        const x = p.edge.a.x + (p.edge.b.x - p.edge.a.x) * p.t
        const y = p.edge.a.y + (p.edge.b.y - p.edge.a.y) * p.t
        const color = readColor(p.hue)
        ctx.shadowBlur = 8
        ctx.shadowColor = color
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(x, y, 2.4, 0, Math.PI * 2)
        ctx.fill()
        ctx.shadowBlur = 0
        const tx = p.edge.a.x + (p.edge.b.x - p.edge.a.x) * Math.max(0, p.t - 0.18)
        const ty = p.edge.a.y + (p.edge.b.y - p.edge.a.y) * Math.max(0, p.t - 0.18)
        const grad = ctx.createLinearGradient(tx, ty, x, y)
        grad.addColorStop(0, color + '00')
        grad.addColorStop(1, color + '55')
        ctx.strokeStyle = grad
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(tx, ty)
        ctx.lineTo(x, y)
        ctx.stroke()
      }

      raf = requestAnimationFrame(loop)
    }

    function onMove(e: MouseEvent) { mouse.x = e.clientX; mouse.y = e.clientY }
    function onLeave() { mouse.x = -9999; mouse.y = -9999 }

    resize()
    window.addEventListener('resize', resize)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseleave', onLeave)
    raf = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseleave', onLeave)
    }
  }, [enabled, density, motion])

  if (!enabled) return null
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.40 }}
    />
  )
}
